/* Remembered face measurement: the latest camera measurement the visitor explicitly applied (a camera result button),
   reused on every mask page so nobody has to measure again on the next mask. A hand measurement (Measure by Hand →
   Use Size) stays on its own page and is never kept here (owner, 10 Oct 2026); older hand records are dropped on read.
   A record holds only a number, its method, the size-rule version, dates, the product it was applied on and whether a
   signed-in account was seen ('signed-in' or 'saved'), never an image, landmarks or device data.
   Kept 30 days in localStorage where the visitor's privacy choices allow preference storage, otherwise only for this tab
   (sessionStorage). Signing out, or removing the measurement saved to the account, also removes it (see noteAccount).
   Every storage access may throw (Safari private mode, blocked site data, in-app browsers); failures mean "nothing kept". */
(function (scope) {
  'use strict';
  if (scope.SpaceArmoryFitMemory) return;
  const key = 'sa_face_fit_remembered_v1';
  const maxAge = 30 * 24 * 60 * 60 * 1000;
  const methods = ['camera'];
  const accountLinks = ['signed-in', 'saved'];
  const areas = ['localStorage', 'sessionStorage'];

  function area(name) {
    try { return scope[name] || null; } catch (error) { return null; }
  }
  // Shopify's Customer Privacy API: false only where the visitor must opt in and has not (no banner in the US: allowed).
  function persistent() {
    try { return scope.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() !== false; } catch (error) { return false; }
  }
  function clean(value, now = Date.now()) {
    const rules = scope.SpaceArmoryFitRules;
    if (!rules || !value || typeof value !== 'object' || value.schema !== 1 || value.rules !== rules.version) return null;
    if (typeof value.cm !== 'number' || !rules.sizeFor(value.cm) || !methods.includes(value.method)) return null;
    if (typeof value.product !== 'string' || !value.product) return null;
    const expires = Date.parse(value.expiresAt);
    if (!Number.isFinite(Date.parse(value.measuredAt)) || !Number.isFinite(expires) || expires <= now || expires > now + maxAge + 60000) return null;
    const record = {schema: 1, cm: value.cm, method: value.method, rules: value.rules, measuredAt: value.measuredAt, product: value.product, expiresAt: value.expiresAt};
    if (accountLinks.includes(value.account)) record.account = value.account;
    return record;
  }
  function load(name) {
    const storage = area(name);
    let raw = null;
    try { raw = storage?.getItem(key) ?? null; } catch (error) { return null; }
    if (raw === null) return null;
    let record = null;
    try { record = clean(JSON.parse(raw)); } catch (error) {}
    // Expired, damaged, or written for older size rules: never used, so drop it.
    if (!record) { try { storage.removeItem(key); } catch (error) {} }
    return record;
  }
  function remove(name) {
    try { area(name)?.removeItem(key); } catch (error) {}
  }
  function put(record, keep = persistent()) {
    const text = JSON.stringify(record);
    for (const name of keep ? areas : ['sessionStorage']) {
      try {
        area(name).setItem(key, text);
        areas.filter(other => other !== name).forEach(remove);
        return name;
      } catch (error) {}
    }
    if (!keep) remove('localStorage');
    return '';
  }
  function accountLink(state) {
    if (!state || state.status !== 'ready' || !state.customer) return '';
    return state.measurement ? 'saved' : 'signed-in';
  }

  // The newest valid record, or null.
  function read() {
    const local = load('localStorage');
    const session = load('sessionStorage');
    const record = local && session ? (Date.parse(session.measuredAt) > Date.parse(local.measuredAt) ? session : local) : local || session;
    // Two copies, or a 30-day copy after the privacy choice changed: keep one copy where the choice allows.
    if (record && local && (session || !persistent())) put(record);
    return record;
  }

  // Called only when the visitor applies a camera measurement. measurement: {cm, method: 'camera', measuredAt?}.
  function write(measurement, handle) {
    const rules = scope.SpaceArmoryFitRules;
    const cm = measurement?.cm;
    if (!rules || typeof cm !== 'number' || !rules.sizeFor(cm) || !methods.includes(measurement.method)) return null;
    const now = Date.now();
    const measured = Date.parse(measurement.measuredAt);
    const record = {
      schema: 1, cm, method: measurement.method, rules: rules.version,
      measuredAt: new Date(Number.isFinite(measured) && measured <= now + 60000 ? measured : now).toISOString(),
      product: String(handle || '') || 'unknown-product',
      expiresAt: new Date(now + maxAge).toISOString(),
    };
    const account = accountLink(scope.SpaceArmoryFitAccount?.state);
    if (account) record.account = account;
    put(record);
    return record;
  }

  function clear() {
    areas.forEach(remove);
  }

  // What an account check (state.status 'ready' only) means for a record: the record to keep, with its account link
  // brought up to date, or null when it has to go. A guest's record is never dropped here, so routine account refreshes
  // (focus, pageshow) keep it. A record kept while signed in goes once the visitor is seen signed out, and a record kept
  // next to a saved account measurement goes once that saved measurement is gone.
  function afterAccount(record, state) {
    if (!record || !state || state.status !== 'ready') return record;
    if (!state.customer) return record.account ? null : record;
    if (!state.measurement && record.account === 'saved') return null;
    const account = state.measurement ? 'saved' : record.account || 'signed-in';
    return account === record.account ? record : {...record, account};
  }
  function noteAccount(state) {
    const record = read();
    const next = afterAccount(record, state);
    if (record && !next) clear();
    else if (next !== record) put(next);
    return next;
  }

  // face-fit-account.js checks the account on every storefront page, so a sign-out or a removed saved measurement is
  // noticed before any mask page uses the copy. Mask pages also call noteAccount from the purchase controller.
  function watchAccount() {
    const account = scope.SpaceArmoryFitAccount;
    if (account && typeof account.addEventListener === 'function') account.addEventListener('change', () => noteAccount(account.state));
  }
  try {
    if (scope.document?.readyState === 'complete') watchAccount();
    else scope.document?.addEventListener('DOMContentLoaded', watchAccount, {once: true});
  } catch (error) {}

  // Declining preference storage in the cookie banner later in the visit keeps the record for this tab only.
  try {
    scope.document?.addEventListener('visitorConsentCollected', event => {
      if (event.detail?.preferencesAllowed !== false) return;
      const record = load('localStorage');
      if (record) put(record, false);
      else remove('localStorage');
    });
  } catch (error) {}

  scope.SpaceArmoryFitMemory = Object.freeze({key, maxAge, read, write, clear, noteAccount, afterAccount});
})(globalThis);
